import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../../abstract/api/rest";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import { StatusTypes } from "../../../abstract/controller/enum/dnd/StatusTypes";
import { GroupTypes } from "../../../abstract/controller/enum/GroupTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import { isNonEmptyString, isNullish } from "../../../abstract/controller/utils/systemUtils";
import useRequestContainer from "../../../abstract/hook/useRequestContainer";
import useStatus from "../../../abstract/hook/useStatus";
import { getEndpointAI } from "../../../abstract/redux/slice/endpointSlice";
import { getErrors, getIsExtended, setMetaExtended } from "../../../abstract/redux/slice/metaSlice";
import {
  getIsContextMenuDisplayed,
  getWindowLock,
  setContextMenu,
  setWindowLock,
} from "../../../abstract/redux/slice/overlaySlice";
import { isStatusDragged, setIdle } from "../../../abstract/redux/slice/statusSlice";
import {
  handleChoiceCopy,
  handleChoiceCreate,
  handleChoiceMove,
  handleChoiceRemove,
  handleChoiceRename,
  handleChoiceRenameAndCreate,
} from "../../../abstract/redux/slice/structureSlice";
import IconMenuVertical from "../../../themes/icons/IconMenuVertical";
import IconPlusCircled from "../../../themes/icons/IconPlusCircled";
import IconWindowMaximize2 from "../../../themes/icons/IconWindowMaximize2";
import IconWindowMinimize2 from "../../../themes/icons/IconWindowMinimize2";
import Labels from "../../label/Labels";
import Meta from "../../meta/Meta";
import TooltipNode from "../../tooltip/TooltipNode";
import { ButtonOverlay } from "../../ui/buttons/ButtonOverlay";
import IconError from "../../ui/icons/IconError";
import Menu from "../../ui/menu/Menu";
import DialogSelect from "../../ui/modal/DialogSelect";
import DefaultTooltip, * as vendorTooltip from "../../ui/tooltips/DefaultTooltip";
import styles from "./ChoiceHeader.module.scss";

const _inputExists = "↳ Name is not unique";
const _inputEmpty = "↳ Name is not defined";
const _inputColonStart = "↳ Name cannot start with ':'";
const _inputColonEnd = "↳ Name cannot end with ':'";
const _inputColonDouble = "↳ Name cannot contain '::'";

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault = "Choice name";

const ChoiceHeaderContext = createContext();

function ChoiceHeader({ node, status, children, isLatest }) {
  const _dispatch = useDispatch();

  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);

  const _errors = useSelector(getErrors(_uuid));

  const _status = useStatus(_id);
  const _isExtended = useSelector(getIsExtended(_uuid));
  const _isWindowLocked = useSelector(getWindowLock);
  const _isContextMenuDisplayed = useSelector(getIsContextMenuDisplayed(_id));

  const [_showModal, _setShowModal] = useState(false);
  const [_labelError, _setLabelError] = useState("");
  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);

  const _name = nodeUtils.getName(node) ?? "";
  const _value = nodeUtils.getValue(node) ?? "";
  const _label = nodeUtils.getLabel(node) ?? "";

  const _isAbstract = nodeUtils.isAbstract(node);

  const _isHidden = isStatusDragged(_status) || _isWindowLocked;

  const clearContextMenu = () => {
    if (_isContextMenuDisplayed) {
      _dispatch(setContextMenu(""));
    }
  };

  const toggleContextMenu = () => {
    _dispatch(setContextMenu(_isContextMenuDisplayed ? "" : _id));
  };

  const getClassHeader = () => {
    const _classType = _isAbstract ? styles["header-abstract"] : styles["header-concrete"];
    const _classOptions = _isExtended
      ? `${styles["header__options"]} ${_isAbstract ? styles["header__options-abstract"] : styles["header__options-concrete"]}`
      : "";
    return `${styles["header"]} ${_classType} ${_classOptions}`;
  };

  return (
    <ChoiceHeaderContext.Provider
      value={{
        isContextMenuDisplayed: _isContextMenuDisplayed,
        clearContextMenu,
        toggleContextMenu,
        node,
        id: _id,
        name: _name,
        value: _value,
        label: _label,
        status,
        isAbstract: _isAbstract,
        placeholder: _placeholder,
        setPlaceholder: _setPlaceholder,
        labelError: _labelError,
        setLabelError: _setLabelError,
        showModal: _showModal,
        setShowModal: _setShowModal,
        isExtended: _isExtended,
        uuid: _uuid,
        isHidden: _isHidden,
        errors: _errors,
        isLatest,
        isWindowLocked: _isWindowLocked,
      }}>
      <div className={getClassHeader()}>
        <Header>{children}</Header>
      </div>
      <WindowOptions />
      <WindowModalRemoveOptions />
    </ChoiceHeaderContext.Provider>
  );
}

function Header({ children }) {
  const _dispatch = useDispatch();
  const { id, label, status, errors, name, node, value, uuid, isAbstract, isExtended } =
    useContext(ChoiceHeaderContext);

  const [_isMouseOver, _setMouseOver] = useState(false);

  const handleOnMouseOver = (e) => {
    e.stopPropagation();

    _setMouseOver(true);
  };

  const handleOnMouseOut = (e) => {
    e.stopPropagation();

    _setMouseOver(false);
  };

  const [{ _canDrop, _isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.CHOICE,
      collect: (monitor) => ({
        _isOver: monitor.isOver(),
        _canDrop: monitor.canDrop(),
      }),
      canDrop(item) {
        const _srcId = nodeUtils.getID(item.element);
        const _group = pathUtils.determineGroup(_srcId, id);

        if (_group !== GroupTypes.INTERNAL_FUNCTIONS && _group !== GroupTypes.INTERNAL_GLOBALS) {
          return false;
        }
        if (value) {
          return false;
        }
        return !isAbstract && id !== nodeUtils.getID(item.element);
      },
      drop(item) {
        _dispatch(setIdle());
        _dispatch(handleChoiceMove(nodeUtils.getID(item.element), id, true));
      },
    }),
    [id, isAbstract]
  );

  const getClass = () => {
    return `${styles["header__form"]} ${styles[`header__form--${isAbstract && !isExtended ? "abstract" : "concrete"}`]} ${
      _canDrop && _isOver ? styles["style--hover"] : ""
    }`;
  };

  const _nonEmpty = isNonEmptyString(name);

  return (
    <>
      <div id={`head-${uuid}`} onMouseOver={handleOnMouseOver} onMouseOut={handleOnMouseOut}>
        <form
          id={`head-form-${uuid}`}
          ref={drop}
          className={getClass()}
          onSubmit={(e) => e.preventDefault()}>
          <div className={styles["icon-error"]}>
            <IconError uuid={uuid} errors={errors} />
          </div>
          <HeaderInputName isMouseOver={_isMouseOver} isOver={_isOver} />
          <HeaderError />
          <HeaderShowValue />
          {label.length > 0 && <Labels node={node} />}
          {_nonEmpty && !isAbstract && _isMouseOver && <HeaderIconAdd />}
          {_nonEmpty && _isMouseOver && <HeaderIconMeta />}
          {_nonEmpty && _isMouseOver && <HeaderIconOptions />}
          <Meta node={node} status={status} show={isExtended} />
        </form>
      </div>
      <ChoiceChildren>{children}</ChoiceChildren>
    </>
  );
}

function HeaderError() {
  const { labelError, uuid } = useContext(ChoiceHeaderContext);
  if (!labelError) {
    return null;
  }
  return (
    <label id={`error-${uuid}`} className={styles["header__error"]}>
      {labelError}
    </label>
  );
}

function HeaderShowValue() {
  const { uuid, value, isAbstract, isExtended } = useContext(ChoiceHeaderContext);
  if (isAbstract || isExtended || !value) {
    return null;
  }
  return (
    <>
      <input
        data-tooltip-id={`tooltip-value-info-${uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
        type="text"
        value={value}
        id={`input-value-${uuid}`}
        className={styles["header__value"]}
        disabled={true}
      />
      {value.length > 20 && (
        <DefaultTooltip handle={`tooltip-value-info-${uuid}`}>{value}</DefaultTooltip>
      )}
    </>
  );
}

function HeaderInputName({ isOver, isMouseOver }) {
  const _dispatch = useDispatch();
  const {
    clearContextMenu,
    status,
    isAbstract,
    labelError,
    setLabelError,
    placeholder,
    setPlaceholder,
    node,
    isHidden,
    errors,
  } = useContext(ChoiceHeaderContext);

  const _endpointAI = useSelector(getEndpointAI);

  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);
  const _name = nodeUtils.getName(node);

  const _container = useRequestContainer({ id: _id, type: "choice" });

  const _nameRef = useRef(null);
  const _abortRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);

  const [_inputName, _setInputName] = useState(_name);
  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const _children = nodeUtils.getChoices(_container.nodeParent);

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const getClassStyle = () => {
    if (status === StatusTypes.DRAGGED) {
      return `${styles["style--dragging"]}`;
    }
    return labelError ? styles["style--stroke"] : "";
  };

  const getClassName = () => {
    const _nonEmpty = isNonEmptyString(_name);
    let classType;

    if (isAbstract) {
      classType = `${styles["header-abstract__name-input"]} ${isMouseOver && _nonEmpty ? styles["header-abstract__name-input--limited"] : ""}`;
    } else {
      classType = `${styles["header-concrete__name-input"]} ${isMouseOver && _nonEmpty ? styles["header-concrete__name-input--limited"] : ""}`;
    }
    const classOver = isOver ? styles["style--selected"] : "";
    const hasErrorClass = errors ? styles["has-error-icon"] : "";

    return `${styles["header__name-input"]} ${classOver} ${classType} ${getClassStyle()} ${hasErrorClass}`;
  };

  const actionChangeNameAccept = useCallback(() => {
    clearSuggestionTimeout();

    if (!labelError && _inputName) {
      _dispatch(handleChoiceRenameAndCreate(_id, _inputName));
    }
  }, [clearSuggestionTimeout, labelError, _inputName, _dispatch, _id]);

  const actionChangeNameCancel = useCallback(() => {
    clearSuggestionTimeout();

    if (_name) {
      _setInputName(_name);
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      _dispatch(handleChoiceRemove(_id));
    }
  }, [_dispatch, _id, _name, clearSuggestionTimeout, setLabelError, setPlaceholder]);

  const handleInputNameOnChange = (e) => {
    const _value = e.target.value;

    _setInputName(_value);

    const _childrenNames = _children?.map((e) => nodeUtils.getName(e)) ?? [];

    if (_value.startsWith(":")) {
      setLabelError(_inputColonStart);
    } else if (_value.endsWith(":")) {
      setLabelError(_inputColonEnd);
    } else if (_value.includes("::")) {
      setLabelError(_inputColonDouble);
    } else if (_childrenNames.includes(_value) && _name !== _value) {
      setLabelError(_inputExists);
    } else if (!_value) {
      setLabelError(_inputEmpty);
    } else {
      setLabelError("");
    }

    setPlaceholder(_placeholderDefault);

    if (labelError === "") {
      if (_value && _value.trim() !== "") {
        if (_nameRef.current.selectionEnd >= _nameRef.current.textLength) {
          suggestNameSuffix(_value);
        } else {
          clearSuggestionTimeout();
        }
      }
    }
  };

  const handleInputNameFocusIn = () => {
    clearContextMenu();

    if (!_name && (!_inputName || _inputName.trim() === "")) {
      suggestName();
    } else {
      _nameRef.current.select();
    }
  };

  const handleInputNameFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    clearSuggestionTimeout();

    if (labelError) {
      if (_name) {
        _setInputName(_name);
        _dispatch(handleChoiceRename(_id, _name));
      } else {
        _dispatch(handleChoiceRemove(_id));
      }
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      if (_inputName && _inputName === _name) {
      } else if (_inputName) {
        _dispatch(handleChoiceRename(_id, _inputName));
      } else if (_name) {
        _dispatch(handleChoiceRename(_id, _name));
      } else {
        _dispatch(handleChoiceRemove(_id));
      }
    }
  };

  const suggestName = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }

    _setIsLoadingSuggestion(true);
    setPlaceholder(_placeholderSuggestion);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_nameRef.current) {
        if (_suggestion.status !== "error" && !_nameRef.current.value) {
          _setInputName(_suggestion.message);

          _nameRef.current.value = _suggestion.message;
          _nameRef.current.select();
        } else {
          setPlaceholder("Suggestion error");
        }
      }
    } catch (error) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout, setPlaceholder]);

  const suggestNameSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }
      
      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setInputName(_suggestion.message);

            if (_nameRef.current) {
              _nameRef.current.value = _suggestion.message;
              _nameRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          }
        } catch (error) {}

        clearSuggestionTimeout();
      }, 2000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    const _target = _nameRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        if (!_isLoadingSuggestion) {
          actionChangeNameAccept();
        }
      } else if (e.code === "Escape") {
        actionChangeNameCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestNameSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    if (_target) {
      _target.addEventListener("keydown", callback);
    }

    return () => (_target ? _target.removeEventListener("keydown", callback) : {});
  }, [
    _isLoadingSuggestion,
    actionChangeNameAccept,
    actionChangeNameCancel,
    clearSuggestionTimeout,
    suggestNameSuffix,
  ]);

  useEffect(() => {
    if (status === StatusTypes.DRAGGED) {
      _dispatch(setMetaExtended(_uuid, false));
      _nameRef.current.blur();
    }
  }, [status, _dispatch, _uuid]);

  useEffect(() => {
    if (!_name) {
      _nameRef.current.focus();
    }
  }, [_name]);

  const displayValue = _inputName || "";

  return (
    <>
      <input
        id={`input-name-${_uuid}`}
        data-tooltip-id={`node-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
        ref={_nameRef}
        type="text"
        className={`${getClassName()} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        value={displayValue}
        placeholder={placeholder}
        onChange={handleInputNameOnChange}
        onFocus={handleInputNameFocusIn}
        onBlur={handleInputNameFocusOut}
        data-title={_name}
      />
      <TooltipNode node={node} handle={`node-${_uuid}`} isHidden={isHidden} />
    </>
  );
}

function HeaderIconMeta() {
  const _dispatch = useDispatch();
  const { uuid, status, isExtended } = useContext(ChoiceHeaderContext);

  const handleButtonShowOptions = () => {
    _dispatch(setMetaExtended(uuid, !isExtended));
  };

  const getClass = () => {
    return `${styles["icons-options-expand"]}`;
  };

  return (
    <>
      {status !== StatusTypes.DRAGGED && (
        <div className={getClass()}>
          <ButtonOverlay handler={handleButtonShowOptions}>
            {isExtended ? <IconWindowMinimize2 /> : <IconWindowMaximize2 />}
          </ButtonOverlay>
        </div>
      )}
    </>
  );
}

function HeaderIconAdd() {
  const _dispatch = useDispatch();
  const { id, uuid, value } = useContext(ChoiceHeaderContext);
  const _inactive = isNullish(value) || value !== "";

  const handleMenuMakeAbstract = () => {
    if (!_inactive) {
      _dispatch(handleChoiceCreate(id));
      _dispatch(setMetaExtended(uuid, false));
    }
  };

  const getClass = () => {
    return `${styles["icons-options-add"]}`;
  };

  return (
    <>
      <div
        className={getClass()}
        data-tooltip-id={`tooltip-make-abstract-info-${uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
        <ButtonOverlay handler={handleMenuMakeAbstract}>
          <IconPlusCircled />
        </ButtonOverlay>
      </div>
      <DefaultTooltip handle={`tooltip-make-abstract-info-${uuid}`}>
        {_inactive ? "Remove choice value to add nested choices" : "Add nested choices"}
      </DefaultTooltip>
    </>
  );
}

function HeaderIconOptions() {
  const { toggleContextMenu, uuid } = useContext(ChoiceHeaderContext);

  const getClass = () => {
    return `${styles["icons-options-menu"]}`;
  };

  const handleMenuToggle = () => {
    toggleContextMenu();
  };

  return (
    <>
      <div
        className={getClass()}
        onClick={handleMenuToggle}
        data-tooltip-id={`tooltip-show-options-info-${uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
        <IconMenuVertical id={`action-${uuid}`} />
      </div>
      <DefaultTooltip handle={`tooltip-show-options-info-${uuid}`}>
        Show choice options
      </DefaultTooltip>
    </>
  );
}

function ChoiceChildren({ children }) {
  const { uuid, isAbstract } = useContext(ChoiceHeaderContext);
  return <>{isAbstract && <div id={`children-${uuid}`}>{children}</div>}</>;
}

function WindowOptions() {
  const _dispatch = useDispatch();
  const { isContextMenuDisplayed, clearContextMenu, id, isAbstract, setShowModal } =
    useContext(ChoiceHeaderContext);
  const handleMenuRemoveChoice = () => {
    if (isAbstract) {
      _dispatch(setWindowLock(true));
      setShowModal(true);
    } else {
      _dispatch(handleChoiceRemove(id));
    }
  };
  const handleMenuCopy = () => {
    _dispatch(handleChoiceCopy(id));
    clearContextMenu();
  };
  const getMenuItems = () => {
    const menuItems = [];
    menuItems.push({ name: "remove", action: handleMenuRemoveChoice });
    menuItems.push({ name: "copy", action: () => handleMenuCopy() });
    return menuItems;
  };
  return <Menu show={isContextMenuDisplayed} items={getMenuItems()} />;
}

function WindowModalRemoveOptions() {
  const dispatch = useDispatch();
  const { id, showModal, setShowModal } = useContext(ChoiceHeaderContext);
  const handleMenuDelete = (keep) => {
    dispatch(handleChoiceRemove(id, keep));
  };
  if (!showModal) {
    return null;
  }
  return (
    <DialogSelect
      textHeader="remove options"
      textBody={`Decide what to do with nested choices.`}
      actions={[
        {
          text: "delete",
          handler: () => {
            handleMenuDelete(false);
            dispatch(setWindowLock(false));
            setShowModal(false);
          },
        },
        {
          text: "keep",
          handler: () => {
            handleMenuDelete(true);
            dispatch(setWindowLock(false));
            setShowModal(false);
          },
        },
      ]}
      cancel={() => {
        dispatch(setWindowLock(false));
        setShowModal(false);
      }}
    />
  );
}

export default ChoiceHeader;
